import { useEffect } from 'react';
import { LngLatBounds, type Map, type PaddingOptions } from 'mapbox-gl';
import { useIsMobile } from 'react-ui';
import { pendingAutoFit } from 'ts-core';
import { useMapBox } from './BalloonEventsMapBox';
import { useBalloonEventsDataSafe } from '../contexts/EventData';

/**
 * Measured pixel padding so the auto-fit frames the balloons inside the area
 * that's actually visible — clear of the top search/category/filter bar, the
 * left list panel (desktop) and the bottom sheet (mobile). Measuring the live
 * chrome (rather than hardcoding) keeps it correct when the toolbar wraps to
 * more rows or the sheet height changes. Without enough top padding the
 * northernmost balloon lands under the toolbar AND on the search-bbox edge,
 * so it's both clipped and dropped from the list.
 *
 * Each inset is capped so a tall bottom sheet (or an oversized toolbar) can't
 * collapse the fit to nothing, and falls back to safe constants when a chrome
 * element isn't present.
 */
function computeAutoFitPadding(map: Map, isMobile: boolean): PaddingOptions {
    const MARGIN = 20;
    const fallback: PaddingOptions = isMobile
        ? { top: 140, bottom: 320, left: 24, right: 24 }
        : { top: 140, bottom: 60, left: 420, right: 60 };

    const doc = map.getContainer().ownerDocument;
    const c = map.getContainer().getBoundingClientRect();
    if (!c.width || !c.height) return fallback;

    const rect = (selector: string): DOMRect | null => {
        const el = doc.querySelector(selector) as HTMLElement | null;
        if (!el) return null;
        const r = el.getBoundingClientRect();
        return r.width > 0 && r.height > 0 ? r : null;
    };
    const capV = c.height * 0.45;
    const capH = c.width * 0.6;
    const inset = (px: number, cap: number) => Math.round(Math.min(Math.max(0, px) + MARGIN, cap));

    // Top search / category / filter bar overlaying the top of the map.
    const topBar = rect('.balloon-map-topbar');
    const top = topBar ? inset(topBar.bottom - c.top, capV) : fallback.top;

    // Left list panel — desktop only (left-anchored, partial width).
    const panel = rect('.balloons-panel');
    const left = panel && panel.left <= c.left + 1 && panel.width < c.width * 0.8
        ? inset(panel.right - c.left, capH)
        : fallback.left;

    // Bottom sheet — mobile only (bottom-anchored).
    const sheet = rect('.bottom-sheet');
    const bottom = sheet && sheet.bottom >= c.bottom - 1
        ? inset(c.bottom - sheet.top, capV)
        : fallback.bottom;

    return { top, bottom, left, right: fallback.right };
}

/**
 * One-shot auto-fit to the whole filtered set after a tag / category / user /
 * partner route is entered.
 *
 * Flow: the entry point (FilterRouteHandler, InitialFilterApplier,
 * useApplyTagFilter, UserScope, PartnerScope) arms `pendingAutoFit` right
 * before it adds the query → the v3 repo appends include_global_bbox=1 to the
 * next full-viewport request → the response carries a globalBoundingBox →
 * this component fits/flies the map once and disarms. Pans and zooms don't
 * re-arm, so they never re-fit.
 *
 * This must live INSIDE EventDataProvider (so it can read globalBoundingBox)
 * while still reaching the map via useMapBox() — which is exactly where
 * UserScopeMapFit sat. BalloonEventsMapBox itself is an ancestor of
 * EventDataProvider and therefore cannot read the event context.
 */
export function MapAutoFitHandler(): null {
    const map = useMapBox()?.map ?? null;
    const isMobile = useIsMobile();
    const globalBoundingBox = useBalloonEventsDataSafe()?.globalBoundingBox ?? null;

    useEffect(() => {
        if (!map || !globalBoundingBox || !pendingAutoFit.isArmed()) return;

        const { topLeft, bottomRight } = globalBoundingBox;

        // Measured padding so fitted balloons clear the on-screen chrome (top
        // bar, left panel on desktop, bottom sheet on mobile) instead of hiding
        // behind it — and so the northernmost balloon stays inside the search
        // viewport and the list.
        const padding = computeAutoFitPadding(map, isMobile);

        // Degenerate bbox (single event / identical corners) → fly to a sensible
        // local zoom rather than fitting a zero-area rect (mapbox handles those oddly).
        if (topLeft[0] === bottomRight[0] && topLeft[1] === bottomRight[1]) {
            map.flyTo({ center: [topLeft[1], topLeft[0]], zoom: 13, padding, duration: 600 });
        } else {
            // bbox corners are [lat, lng]; build the bounds via extend so corner
            // ordering is normalized correctly for fitBounds.
            const sw: [number, number] = [topLeft[1], topLeft[0]];
            const bounds = new LngLatBounds(sw, sw);
            bounds.extend([bottomRight[1], bottomRight[0]]);

            // Inflate the box a touch so the extreme balloons don't sit flush on
            // the padded viewport edge. fitBounds frames the box edges exactly
            // against the padding, which would leave the outermost balloons on
            // the knife-edge of the list's `bounds.contains` viewport filter
            // (intermittently dropping them) and visually pinned to the toolbar.
            const ne = bounds.getNorthEast();
            const sw2 = bounds.getSouthWest();
            const dLng = Math.max((ne.lng - sw2.lng) * 0.06, 0.0006);
            const dLat = Math.max((ne.lat - sw2.lat) * 0.06, 0.0006);
            const inflated = new LngLatBounds(
                [sw2.lng - dLng, sw2.lat - dLat],
                [ne.lng + dLng, ne.lat + dLat],
            );
            map.fitBounds(inflated, { padding, maxZoom: 15, duration: 600 });
        }

        pendingAutoFit.disarm();
    }, [map, globalBoundingBox, isMobile]);

    return null;
}

export default MapAutoFitHandler;
